/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ComponentPropsWithoutRef, useEffect, useState } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { useForm } from "react-hook-form";
import { Description, Label, Spacer, Text, theme } from "@nordcloud/gnui";
import { ContactsInput, Fieldset, InputField } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";

export type FormData = {
  ownerId: string;
  contactIds: string[];
  name: string;
  description: string;
};

type Props = Omit<ComponentPropsWithoutRef<"form">, "onSubmit"> & {
  onSubmit: (data: FormData) => void;
};

export function ApplicationDetailsForm({ onSubmit, ...props }: Props) {
  const [ownerId, setOwnerId] = useState<string>("");
  const [contactIds, setContactIds] = useState<string[]>([]);
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);

  const {
    handleSubmit,
    register,
    setValue,
    clearErrors,

    formState: { errors },
  } = useForm();

  useEffect(() => {
    register("ownerId", { required: true });
  }, [register]);

  const submitData = (data: { name: string; description: string }) => {
    onSubmit({
      ownerId,
      contactIds,
      name: data.name,
      description: data.description,
    });
  };

  const onOwnerChange = (owner: string) => {
    try {
      setOwnerId(owner);
      clearErrors("ownerId");
      setValue("ownerId", owner);
      showSuccess(SUCCESS_TEXT.accountOwnerAdded);
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const onContactChange = (contact: string) => {
    try {
      setContactIds([contact]);
      setValue("contactIds", contact);
      showSuccess(SUCCESS_TEXT.contactPersonAdded);
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  return (
    <form onSubmit={handleSubmit(submitData)} {...props}>
      <Row>
        <Col sm={8} md={4} lg={6} offset={{ sm: 0, md: 2 }}>
          <InputField
            label="Application Name"
            {...register("name", { required: true })}
            required
            type="input"
            error={errors.name}
          />
          <Fieldset>
            <Label required name="Application Owner" />
            <ContactsInput
              placeholder="Select owner person"
              value={[ownerId.toString()]}
              status={errors.ownerId && "error"}
              onChange={onOwnerChange}
            />
            {errors.ownerId && <Description>Owner is required.</Description>}
          </Fieldset>
          <Text
            css={{ cursor: "pointer" }}
            color="notification"
            onClick={() => setShowAdditionalSettings((prevState) => !prevState)}
          >
            {showAdditionalSettings
              ? "- Hide additional settings"
              : "+ Show additional settings"}
          </Text>
          {showAdditionalSettings && (
            <>
              <Fieldset>
                <Label name="Contact Person" />
                <ContactsInput
                  placeholder="Select contact person"
                  value={[contactIds.toString()]}
                  onChange={onContactChange}
                />
                {errors.contactIds && (
                  <Description>Contact person is required.</Description>
                )}
              </Fieldset>
              <Fieldset>
                <InputField
                  placeholder="Provide application description"
                  label="Application Description"
                  {...register("description")}
                  type="input"
                />
              </Fieldset>
              <Spacer height={theme.spacing.spacing07} />
            </>
          )}
        </Col>
      </Row>
    </form>
  );
}
